import React from 'react'
import { Provider, connect } from 'react-redux'
import { bindActionCreators } from 'redux'
import { Row, Col } from '@cx/couturier/Grid'
import Base from '@cx/couturier/Base'
import { Header, Container, Transition } from '@cx/couturier/labs/ImmersiveFlow'
import CloseIcon from '@cx/couturier/icons/utils/Close'
import {
  RightAlignCol,
  ClickableAnchor,
  CompanyLogoModified,
  ResponsiveBody,
  FormattedForm,
  ShowForMediumUp,
  ShowForSmallOnly
} from 'cxid-common-components/util-components'
import styled, { css } from 'styled-components'
import WelcomeBackModal from './modals/deactivated-user-modal'
import VIEWS from '../store/views'
import { MobileRelativeFooter, StickyDesktopFooter, ButtonFooter } from './app-footer'
import HelpPanel from './HelpPanel'
import ContentContainer from './content-container'
import { viewBack, toggleHelpModal, postForm, postUsername } from '../store/actions'

const HiddenSubmit = styled.button`
  display: none;
`

const TransitionWrapper = styled.div`
  & > div > div {
    ${({ theme: { media } }) =>
      media.medium(css`
        height: calc(100% - 4rem);
      `)}
  }
`

class App extends React.PureComponent {
  constructor(props) {
    super(props)
    this.state = { isMounted: false }
  }

  componentDidMount() {
    setTimeout(() => this.setState({ isMounted: true }), 25)
  }

  render() {
    const {
      dobValid,
      ssnValid,
      viewName,
      usernameValid,
      dispatchPostForm,
      dispatchPostUsername,
      onBackButtonClick,
      backButtonClicked,
      wait,
      back,
      isLunaLiteEnabled,
      telephone,
      hours,
      step
    } = this.props
    const { isMounted } = this.state
    const allowedToContinue = viewName === VIEWS.username.name ? usernameValid : dobValid && ssnValid && usernameValid

    const buttonText = wait ? 'Verifying' : 'Verify'
    const buttonEnabled = allowedToContinue && !wait

    return (
      <Base luna={isLunaLiteEnabled}>
        <WelcomeBackModal />
        <Container>
          <Header
            currentStep={step}
            PostfixComponent={
              <RightAlignCol medium={4} small={2}>
                <ClickableAnchor
                  aria-label="Close Icon | Back to login"
                  onClick={() => {
                    window.location = '/login'
                  }}
                  role="button"
                >
                  <CloseIcon />
                </ClickableAnchor>
              </RightAlignCol>
            }
            PrefixComponent={
              <React.Fragment>
                <ShowForMediumUp medium={4} small={2}>
                  <ClickableAnchor
                    aria-label="Northwestern Mutual Logo | Back to homepage"
                    href="https://www.northwesternmutual.com"
                    id="company-logo-full"
                  >
                    <CompanyLogoModified full />
                  </ClickableAnchor>
                </ShowForMediumUp>
                <ShowForSmallOnly medium={4} small={2}>
                  <ClickableAnchor
                    aria-label="Northwestern Mutual Logo | Back to homepage"
                    href="https://www.northwesternmutual.com"
                    id="company-logo-mobile"
                  >
                    <CompanyLogoModified />
                  </ClickableAnchor>
                </ShowForSmallOnly>
              </React.Fragment>
            }
            title="Reset Your Password"
            totalSteps="10"
          />

          <HelpPanel />
          <TransitionWrapper>
            <Transition animation="slide" direction={backButtonClicked ? 'backward' : 'forward'} id={isMounted ? viewName : 'empty'}>
              {isMounted ? (
                <ResponsiveBody>
                  <FormattedForm
                    autoComplete="off"
                    method="POST"
                    onSubmit={evt => {
                      if (evt) {
                        evt.preventDefault()
                      }
                      if (viewName === VIEWS.username.name) {
                        return dispatchPostUsername()
                      }

                      return dispatchPostForm()
                    }}
                  >
                    <Row align="center">
                      <Col large={10} small={12}>
                        <ContentContainer viewName={viewName} />
                      </Col>
                    </Row>

                    <HiddenSubmit disabled={!buttonEnabled} type="submit" />
                    <MobileRelativeFooter hours={hours} telephone={telephone} />
                  </FormattedForm>
                </ResponsiveBody>
              ) : (
                <div className="empty" />
              )}
            </Transition>
          </TransitionWrapper>

          <ButtonFooter
            backOnClick={onBackButtonClick}
            backText={back ? 'Back' : ''}
            className="footer-nopadding"
            continueDisabled={!buttonEnabled}
            continueOnClick={evt => {
              if (evt) {
                evt.preventDefault()
              }
              if (viewName === VIEWS.username.name) {
                return dispatchPostUsername()
              }

              return dispatchPostForm()
            }}
            continueText={buttonText}
            continueWorking={wait}
          />
          <StickyDesktopFooter hours={hours} telephone={telephone} />
        </Container>
      </Base>
    )
  }
}

const mapDispatchToProps = dispatch =>
  bindActionCreators(
    {
      dispatchPostForm: postForm,
      dispatchPostUsername: postUsername,
      onBackButtonClick: viewBack,
      onHelpModalClose: toggleHelpModal,
      onHelpModalOpen: toggleHelpModal
    },
    dispatch
  )

const mapStateToProps = state => ({
  telephone: state.meta.supportHours.item.telephone,
  hours: state.meta.supportHours.item.hours,
  usernameValid: state.form.usernameValid,
  dobValid: state.form.dobValid,
  ssnValid: state.form.ssnValid,
  showHelpModal: state.submit.viewHelp,
  fromUri: state.meta.fromUri,
  viewName: state.view.name,
  back: state.view.back,
  step: state.view.step,
  backButtonClicked: state.submit.backButtonClicked,
  wait: state.submit.wait,
  isLunaLiteEnabled: state.meta.isLunaLiteEnabled
})

const ConnectedApp = connect(mapStateToProps, mapDispatchToProps)(App)

const ProviderComponent = ({ store }) => (
  <Provider store={store}>
    <ConnectedApp />
  </Provider>
)

ProviderComponent.displayName = 'ProviderComponent'

export default ProviderComponent
